import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Settings, 
  Zap, 
  Database, 
  RefreshCw, 
  TrendingUp,
  Clock,
  Target,
  Trash2,
  Gauge
} from 'lucide-react';
import { useExtractionOptimization } from '@/hooks/useExtractionOptimization';
import { useToast } from '@/hooks/use-toast';

interface ExtractionOptimizationPanelProps {
  className?: string;
}

export function ExtractionOptimizationPanel({ className }: ExtractionOptimizationPanelProps) {
  const {
    settings,
    updateSetting,
    optimizeSettings,
    isOptimizing,
    metrics,
    clearCache,
    cacheSize
  } = useExtractionOptimization();
  
  const { toast } = useToast();

  const getPerformanceScore = () => {
    const timeScore = Math.max(0, 100 - metrics.averageTime / 100);
    const errorScore = Math.max(0, 100 - metrics.errorRate * 100);
    const cacheScore = metrics.cacheHitRate * 100;
    const retryScore = metrics.retrySuccessRate * 100;
    
    return Math.round((timeScore + errorScore + cacheScore + retryScore) / 4);
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const performanceScore = getPerformanceScore();

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Extraction Optimization
          <Badge variant={performanceScore >= 80 ? "default" : performanceScore >= 60 ? "secondary" : "destructive"}>
            {performanceScore}% Score
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="performance" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="cache">Cache</TabsTrigger>
          </TabsList>
          
          <TabsContent value="performance" className="space-y-4">
            {/* Performance Overview */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Avg Time</span>
                </div>
                <div className="text-lg font-bold">
                  {formatTime(metrics.averageTime)}
                </div>
              </div>

              <div className="p-3 bg-success/10 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="h-4 w-4 text-success" />
                  <span className="text-sm font-medium">Success Rate</span>
                </div>
                <div className="text-lg font-bold">
                  {((1 - metrics.errorRate) * 100).toFixed(1)}%
                </div>
              </div>

              <div className="p-3 bg-warning/10 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Database className="h-4 w-4 text-warning" />
                  <span className="text-sm font-medium">Cache Hits</span>
                </div>
                <div className="text-lg font-bold">
                  {(metrics.cacheHitRate * 100).toFixed(1)}%
                </div>
              </div>

              <div className="p-3 bg-blue-500/10 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium">Retry Success</span>
                </div>
                <div className="text-lg font-bold">
                  {(metrics.retrySuccessRate * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Overall Performance Score */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Overall Performance</span>
                <span className="text-sm text-muted-foreground">{performanceScore}%</span>
              </div>
              <Progress value={performanceScore} className="h-3" />
            </div>

            {/* Auto-optimization */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-medium text-sm">Auto-Optimization</div>
                  <div className="text-xs text-muted-foreground">
                    Automatically tune settings based on performance
                  </div>
                </div>
                <Button 
                  onClick={optimizeSettings}
                  disabled={isOptimizing}
                  size="sm"
                >
                  {isOptimizing ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  {isOptimizing ? 'Optimizing...' : 'Optimize'}
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Total extractions: {metrics.extractionCount}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="settings" className="space-y-4">
            {/* Core Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Enable Caching</div>
                  <div className="text-xs text-muted-foreground">
                    Cache extraction results for faster processing
                  </div>
                </div>
                <Switch
                  checked={settings.cacheEnabled}
                  onCheckedChange={(checked) => updateSetting('cacheEnabled', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Batch Processing</div>
                  <div className="text-xs text-muted-foreground">
                    Process multiple extractions together
                  </div>
                </div>
                <Switch
                  checked={settings.batchProcessing}
                  onCheckedChange={(checked) => updateSetting('batchProcessing', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Adaptive Retry</div>
                  <div className="text-xs text-muted-foreground">
                    Smart retry logic based on error type
                  </div>
                </div>
                <Switch
                  checked={settings.adaptiveRetry}
                  onCheckedChange={(checked) => updateSetting('adaptiveRetry', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Parallel Processing</div>
                  <div className="text-xs text-muted-foreground">
                    Process fields in parallel for speed
                  </div>
                </div>
                <Switch
                  checked={settings.parallelProcessing}
                  onCheckedChange={(checked) => updateSetting('parallelProcessing', checked)}
                />
              </div>
            </div>

            <Separator />

            {/* Advanced Settings */}
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Confidence Threshold</span>
                  <span className="text-sm text-muted-foreground">
                    {Math.round(settings.confidenceThreshold * 100)}%
                  </span>
                </div>
                <Slider
                  value={[settings.confidenceThreshold]}
                  onValueChange={([value]) => updateSetting('confidenceThreshold', value)}
                  min={0.1}
                  max={1}
                  step={0.1}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Max Retries</span>
                  <span className="text-sm text-muted-foreground">
                    {settings.maxRetries}
                  </span>
                </div>
                <Slider
                  value={[settings.maxRetries]}
                  onValueChange={([value]) => updateSetting('maxRetries', value)}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Timeout</span>
                  <span className="text-sm text-muted-foreground">
                    {settings.timeoutMs / 1000}s
                  </span>
                </div>
                <Slider
                  value={[settings.timeoutMs]}
                  onValueChange={([value]) => updateSetting('timeoutMs', value)}
                  min={5000}
                  max={120000}
                  step={5000}
                  className="w-full"
                />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="cache" className="space-y-4">
            {/* Cache Statistics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-sm font-medium mb-1">Cache Size</div>
                <div className="text-lg font-bold">{cacheSize} items</div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-sm font-medium mb-1">Hit Rate</div>
                <div className="text-lg font-bold">
                  {(metrics.cacheHitRate * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Cache Status */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Cache Status</span>
                <Badge variant={settings.cacheEnabled ? "default" : "secondary"}>
                  {settings.cacheEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
              {settings.cacheEnabled && (
                <Progress value={(cacheSize / 100) * 100} className="h-2" />
              )}
            </div>

            {/* Cache Management */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-medium text-sm">Cache Management</div>
                  <div className="text-xs text-muted-foreground">
                    Manage extraction result cache
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={clearCache}
                  disabled={cacheSize === 0}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Cache
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Cache stores up to 100 recent extraction results with 30-minute TTL
              </div>
            </div>

            {/* Prefetch Settings */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Prefetch Fields</div>
                <div className="text-xs text-muted-foreground">
                  Preload common field patterns
                </div>
              </div>
              <Switch
                checked={settings.prefetchFields}
                onCheckedChange={(checked) => updateSetting('prefetchFields', checked)}
              />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}